import React, { useEffect, useRef, useState } from 'react';

import _ from 'lodash';

import Highcharts from 'highcharts';
import highchartsMap from 'highcharts/modules/map';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import HighchartsReact from 'highcharts-react-official';

if (typeof Highcharts === 'object') {
  highchartsMap(Highcharts);
  highchartsExporting(Highcharts);
  highchartsOfflineExporting(Highcharts);
}

import chartsUtils, { DataCode, Dataset } from 'utils/charts.utils';

// chart options
import { mapChartoptions } from './mapChartNewOptions';

// styles
import './mapChartNew.scss';

interface MapChartNewProps {
  title: string;
  options?: any;
  dataset: Dataset;
  year: number;
  dataCode: DataCode;
  onNoData: () => void;
  onCallback: (chart: React.MutableRefObject<null>) => void;
  roundValues?: boolean;
  switzerlandValueFormat?: string;
}

const MapChartNew = (props: MapChartNewProps) => {
  const chartComponent = useRef(null);
  const [chartOptions, setChartOptions] = useState<any>(_.merge({}, mapChartoptions, props.options));

  const getChartSeriesData = (currentYear: number, dataCode: DataCode) => {
    let result: Array<any> = [];
    const localizations = chartsUtils.getLocalizations(props.dataset, dataCode);
    if (!localizations) {
      return null;
    }
    const data = chartsUtils.getYearData(props.dataset, dataCode, currentYear);
    if (!data) {
      return null;
    }

    localizations.forEach((localization) => {
      let localizationValue = data[localization.index];
      if (localizationValue && props.roundValues === true) {
        localizationValue = Math.round(localizationValue);
      }
      result.push(['ch-' + localization.code.toLocaleLowerCase(), localizationValue]);
    });

    return result;
  };

  const getMinValue = (data: Array<any>) => {
    return data.reduce(function (p, v) {
      return p[1] < v[1] ? p : v;
    });
  };

  const getSwitzerlandValue = (data: Array<any> | null) => {
    if (data == null) return '';

    const result = data.find((d) => d[0] === 'ch-ch');
    if (!result) {
      return '';
    }

    if (props.switzerlandValueFormat && result[1]) {
      var values = props.switzerlandValueFormat;
      var regEx = values.search('g');
      result[1] = result[1].toString().replace(regEx, ' ');
    }

    return result[1] ? result[1] : 'N/A';
  };

  const getTitle = () => {
    let result = props.title;

    result = result.replace('{DATASET}', props.dataset.name);
    result = result.replace('{FILTER}', chartsUtils.getDataCodeName(props.dataCode));
    result = result.replace('{YEAR}', props.year.toString());

    return result;
  };

  const handleCallback = (chart: Highcharts.Chart) => {
    props.onCallback(chartComponent);
  };

  useEffect(() => {
    const data = getChartSeriesData(props.year, props.dataCode);
    if (data == null) {
      props.onNoData();
      return;
    }

    const switzerlandValue = getSwitzerlandValue(data);

    let newOptions = _.merge(
      { ...chartOptions },
      {
        colorAxis: {
          min: data ? getMinValue(data)[1] : 0,
        },
        exporting: {
          chartOptions: {
            title: {
              text: getTitle(),
            },
          },
        },
        tooltip: {
          pointFormatter: function () {
            const formatValue = (value: number | string) => {
              // if values are rounded to integers, show them without decimals
              const decimals = props.roundValues === true ? 0 : 2;
              return value === 'N/A' ? value : Highcharts.numberFormat(value, decimals);
            };

            return `
              <span class="hicharttooltip-title">${this.name}</span>
              <table class="hicharttooltip-table">
                <tr>
                  <th>${chartOptions.tooltip.headerLabel1}</th>
                  <th>${chartOptions.tooltip.headerLabel2}</th>
                  <th style="text-align:right;">Schweiz Ø</th>
                </tr>
                <tr class="hicharttooltip-table-values">
                  <td>${props.year}</td>
                  <td>${formatValue(this.value)}</td>
                  <td style="text-align:right;">${formatValue(switzerlandValue)}</td>
                </tr>
              </table>
            `;
          },
        },
      }
    );
    newOptions.series[0].data = data ? data : [];

    setChartOptions(newOptions);
  }, [props.dataset, props.year, props.dataCode]);

  return (
    <div className="mapChartNew">
      <div className="chart-title">{getTitle()}</div>
      <div className="mapChartNew-chart-wrapper">
        <div className="mapChartNew-chart">
          <HighchartsReact
            ref={chartComponent}
            highcharts={Highcharts}
            constructorType={'mapChart'}
            options={chartOptions}
            callback={handleCallback}
          />
        </div>
      </div>
    </div>
  );
};

export default MapChartNew;
